/* Feb-2026 · GrandfatherBanner — slim dismissible strip shown inside
 * The Loom workspace when an author is on a 90-day price-lock grace period.
 *
 * Fetches `/api/subscriptions/me/price-lock` once on mount.
 * Renders nothing when:
 *   - the flag is disabled, user has no active sub, or no lock date
 *   - the user has dismissed it within the last 30 days
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ShieldCheck, X } from 'lucide-react';

const API          = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const DISMISS_KEY  = 'grandfather-banner-dismissed-at';
const DISMISS_MS   = 30 * 24 * 60 * 60_000;   // re-shows after 30 days

export default function GrandfatherBanner() {
  const [data,      setData]      = useState(null);
  const [dismissed, setDismissed] = useState(false);

  // Check dismiss state on mount
  useEffect(() => {
    const stored = localStorage.getItem(DISMISS_KEY);
    if (stored && Date.now() - Number(stored) < DISMISS_MS) setDismissed(true);
  }, []);

  // Fetch price-lock info
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/subscriptions/me/price-lock`, { withCredentials: true })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!data || !data.enabled || !data.active || !data.locked_until) return null;
  if (dismissed) return null;
  if ((data.days_remaining ?? 0) <= 0) return null;

  const dt = new Date(data.locked_until).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  return (
    <div
      data-testid="grandfather-banner"
      className="flex items-center gap-2 border-b border-amber-500/60 bg-amber-50 px-4 py-1.5 text-[12px] text-amber-900"
    >
      <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-amber-600" />
      <span className="font-semibold">Your rate is locked.</span>
      <span className="hidden sm:inline">
        {data.price_usd
          ? <>You're paying <b>${data.price_usd.toFixed(2)}/{data.cadence || 'mo'}</b> — guaranteed until <b>{dt}</b> ({data.days_remaining} days remaining).</>
          : <>Your current subscription price is guaranteed until <b>{dt}</b> — {data.days_remaining} days remaining.</>}
      </span>
      <button
        type="button"
        onClick={dismiss}
        className="ml-auto rounded-sm p-1 hover:bg-amber-200 transition-colors"
        aria-label="Dismiss grandfather banner"
        data-testid="grandfather-banner-dismiss"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}
